import { useState, useEffect } from "react";
import { Link } from "@/components/LangLink";
import { ChevronLeft, ArrowRight, CalendarCheck } from "lucide-react";
import { CTASection, SectionHeading } from "@/components/sections";
import { ProductGallery, ColorSelector, RequestInfoModal } from "@/components/product-detail";
import { LUMAVEN_COMFORT, LUMAVEN_HEALTH_NOTE } from "@/lib/lumaven";
import { useAssistant } from "@/contexts/AssistantContext";

const LumavenComfort = () => {
  const [modelId, setModelId] = useState(LUMAVEN_COMFORT.models[0].id);
  const [color, setColor] = useState(LUMAVEN_COMFORT.models[0].colors[0].id);
  const [modalOpen, setModalOpen] = useState(false);

  const model = LUMAVEN_COMFORT.models.find((m) => m.id === modelId);

  const { setProductCtx } = useAssistant();
  useEffect(() => {
    const text = LUMAVEN_COMFORT.models
      .map((m) => `MODELLO ${m.name}: colori ${m.colors.map((c) => c.name).join(", ")}; taglie ${m.sizes.join(", ")}; composizione ${m.composition.map((c) => `${c[0]} ${c[1]}`).join(", ")}. ${m.description}`)
      .join("\n\n");
    setProductCtx({
      title: "LUMAVEN Calzini Comfort",
      url: "/it/prodotti/lumaven/calzini-comfort",
      text: `SCHEDA PRODOTTO: LUMAVEN Calzini Comfort (calze preventive, marchio MEDISISTEM). Due modelli: Comfort e Comfort Forte. Orientati soprattutto al pubblico maschile, utilizzabili anche dalla donna.\n\n${text}`,
    });
    return () => setProductCtx(null);
  }, [setProductCtx]);

  const selectModel = (id) => {
    setModelId(id);
    const m = LUMAVEN_COMFORT.models.find((x) => x.id === id);
    setColor(m.colors[0].id);
  };

  const summary = {
    Prodotto: `LUMAVEN ${model.name}`,
    Colore: model.colors.find((c) => c.id === color)?.name,
  };

  return (
    <>
      <section className="pt-[92px] lg:pt-[104px] pb-10 lg:pb-14 bg-white" data-testid="lumaven-comfort">
        <div className="container-x">
          <nav className="flex items-center gap-1.5 text-sm text-muted-foreground" aria-label="breadcrumb">
            <Link to="/prodotti/lumaven" className="hover:text-green">LUMAVEN</Link>
            <span>/</span>
            <span className="text-navy">Calzini Comfort</span>
          </nav>

          <div className="mt-6 max-w-2xl">
            <SectionHeading
              maxW="max-w-2xl"
              eyebrow="Calzini preventivi"
              title="Comfort e Comfort Forte"
              intro={LUMAVEN_COMFORT.tagline}
            />
          </div>

          {/* Selettore modello */}
          <div className="mt-8 inline-flex rounded-full border border-[#E7EDF2] bg-cloud p-1" data-testid="lumaven-comfort-models" role="group" aria-label="Modello">
            {LUMAVEN_COMFORT.models.map((m) => {
              const on = m.id === modelId;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => selectModel(m.id)}
                  aria-pressed={on}
                  data-testid={`lumaven-comfort-model-${m.id}`}
                  className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${on ? "bg-green text-white" : "text-navy/70 hover:text-green"}`}
                >
                  {m.name}
                </button>
              );
            })}
          </div>

          <div className="mt-8 grid gap-10 lg:grid-cols-2">
            <div>
              <ProductGallery images={["/products/lumaven-comfort/comfort.webp"]} testid="lumaven-comfort-gallery" />
              <p className="mt-3 text-xs italic text-muted-foreground">Foto indicativa (colore Nero).</p>
            </div>

            <div data-testid={`lumaven-comfort-detail-${model.id}`}>
              <h2 className="font-serif text-2xl font-bold text-navy">{model.name}</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">{model.description}</p>

              <div className="mt-7">
                <div className="text-sm font-semibold text-navy">Colore</div>
                <div className="mt-3"><ColorSelector colors={model.colors} value={color} onChange={setColor} /></div>
              </div>

              <div className="mt-7" data-testid="lumaven-comfort-sizes">
                <div className="text-sm font-semibold text-navy">Taglie</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {model.sizes.map((s) => (
                    <span key={s} className="rounded-lg border border-[#E7EDF2] bg-cloud px-3 py-1.5 text-sm text-navy">{s}</span>
                  ))}
                </div>
              </div>

              <div className="mt-7">
                <div className="text-sm font-semibold text-navy">Composizione</div>
                <ul className="mt-3 space-y-1.5">
                  {model.composition.map((m) => (
                    <li key={m[0]} className="flex items-center justify-between border-b border-[#E7EDF2] py-1.5 text-sm">
                      <span className="text-muted-foreground">{m[0]}</span>
                      <span className="font-semibold text-navy">{m[1]}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button onClick={() => setModalOpen(true)} data-testid="lumaven-comfort-request-info" className="btn-green inline-flex justify-center">
                  Richiedi informazioni <ArrowRight size={16} />
                </button>
                <Link to="/il-metodo#prenota" className="btn-outline inline-flex justify-center">
                  Prenota una misurazione <CalendarCheck size={16} />
                </Link>
              </div>

              <p className="mt-8 text-xs text-muted-foreground leading-relaxed">{LUMAVEN_HEALTH_NOTE}</p>
              <Link to="/prodotti/lumaven" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-green hover:gap-2.5 transition-all">
                <ChevronLeft size={16} /> Torna a LUMAVEN
              </Link>
            </div>
          </div>
        </div>
      </section>

      <RequestInfoModal open={modalOpen} onOpenChange={setModalOpen} summary={summary} />
      <CTASection />
    </>
  );
};

export default LumavenComfort;
